import { useCallback, useEffect, useRef, useState } from "react";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";

import {
  deleteConversationView,
  fetchConversationViews,
  ConversationViewsApiError,
  type ConversationViewSummary,
} from "@/api/conversation-views";
import type { AccessTokenGetter } from "@/lib/utils";
import i18n from "@/lib/i18n";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, readCache, writeCache } from "@/lib/ui-cache";
import { viewLabelPreview, type CachedViewLabel } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";

export interface UseConversationViewsResult {
  views: ConversationViewSummary[];
  loading: boolean;
  /** True after the current session's initial list request settles. */
  loaded: boolean;
  error: string | null;
  refresh: () => Promise<ConversationViewSummary[]>;
  unpin: (viewId: string) => Promise<void>;
}

/** Session-race-safe authoritative pin list with optimistic deletion. */
export function useConversationViews(
  sessionId: string | null,
  getToken: AccessTokenGetter,
  bootstrapViews?: { views: ConversationViewSummary[] } | null,
  bootstrapPending = false,
): UseConversationViewsResult {
  const verifiedScope = useCacheScope();
  const [views, setViews] = useState<ConversationViewSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(() => !sessionId);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const requestGenerationRef = useRef(0);
  const sessionIdRef = useRef(sessionId);
  const viewsRef = useRef(views);
  const liveConfirmedRef = useRef(false);
  sessionIdRef.current = sessionId;
  viewsRef.current = views;

  const refresh = useCallback(async (): Promise<ConversationViewSummary[]> => {
    const requestedSessionId = sessionId;
    if (!requestedSessionId) {
      controllerRef.current?.abort();
      setViews([]);
      setLoading(false);
      setLoaded(true);
      setError(null);
      return [];
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const generation = ++requestGenerationRef.current;
    setLoading(true);
    setLoaded(false);
    setError(null);
    try {
      const next = await fetchConversationViews(
        requestedSessionId,
        getToken,
        controller.signal,
      );
      if (
        controller.signal.aborted
        || generation !== requestGenerationRef.current
        || sessionIdRef.current !== requestedSessionId
      ) {
        throw new DOMException("Conversation views request superseded", "AbortError");
      }
      viewsRef.current = next;
      liveConfirmedRef.current = true;
      void writeCache("viewSummaries", "views", requestedSessionId, next.map(viewLabelPreview).filter((row): row is CachedViewLabel => !!row));
      setViews(next);
      setLoading(false);
      setLoaded(true);
      return next;
    } catch (caught) {
      if (controller.signal.aborted || generation !== requestGenerationRef.current || sessionIdRef.current !== requestedSessionId) {
        throw new DOMException("Conversation views request superseded", "AbortError");
      }
      const message = caught instanceof Error ? caught.message : i18n.t("views.errors.load");
      if (caught instanceof ConversationViewsApiError && [401, 403, 404].includes(caught.status)) {
        if (caught.status === 401 || caught.status === 403) clearCacheOnAuthFailure();
        setViews([]); void invalidateCache("viewSummaries", "views", requestedSessionId);
      }
      setError(message);
      setLoading(false);
      setLoaded(true);
      throw caught;
    }
  }, [getToken, sessionId]);

  useRefreshOnReconnect(refresh, Boolean(sessionId));

  useEffect(() => {
    requestGenerationRef.current += 1;
    controllerRef.current?.abort();
    viewsRef.current = [];
    liveConfirmedRef.current = false;
    setViews([]);
    setError(null);
    setLoaded(!sessionId);
    if (sessionId && bootstrapViews) {
      liveConfirmedRef.current = true;
      viewsRef.current = bootstrapViews.views;
      setViews(bootstrapViews.views);
      setLoaded(true);
      setLoading(false);
      return () => controllerRef.current?.abort();
    }
    if (sessionId && bootstrapPending) return () => controllerRef.current?.abort();
    if (sessionId && verifiedScope) {
      let stale = true;
      void readCache<CachedViewLabel[]>("viewSummaries", "views", sessionId).then((labels) => {
        if (!stale || cacheScope() !== verifiedScope || liveConfirmedRef.current || !Array.isArray(labels)) return;
        const previews = labels.filter((row) => typeof row?.id === "string" && typeof row.title === "string" && typeof row.icon === "string" && typeof row.type === "string" && typeof row.order === "number").map((row) => ({
          ...row, availability: "unavailable" as const, initial_path: null, can_unpin: false, created_at: null,
        })) as ConversationViewSummary[];
        setViews(previews);
      });
      void refresh().catch(() => {});
      return () => { stale = false; controllerRef.current?.abort(); };
    }
    if (sessionId) void refresh().catch(() => {});
    else setLoading(false);
    return () => controllerRef.current?.abort();
  }, [refresh, sessionId, verifiedScope, bootstrapViews, bootstrapPending]);

  const unpin = useCallback(async (viewId: string): Promise<void> => {
    const requestedSessionId = sessionIdRef.current;
    if (!requestedSessionId) return;
    const snapshot = viewsRef.current;
    const optimistic = snapshot.filter((view) => view.id !== viewId);
    viewsRef.current = optimistic;
    setViews(optimistic);
    setError(null);
    try {
      await deleteConversationView(requestedSessionId, viewId, getToken);
      void invalidateCache("viewSummaries", "views", requestedSessionId);
      // Re-apply the successful removal in case another overlapping unpin
      // temporarily restored an older optimistic snapshot.
      if (sessionIdRef.current === requestedSessionId) {
        const confirmed = viewsRef.current.filter((view) => view.id !== viewId);
        viewsRef.current = confirmed;
        setViews(confirmed);
      }
    } catch (caught) {
      if (sessionIdRef.current === requestedSessionId) {
        const message = caught instanceof Error ? caught.message : i18n.t("views.errors.unpin");
        viewsRef.current = snapshot;
        setViews(snapshot);
        setError(message);
        await refresh().catch(() => {});
        if (sessionIdRef.current === requestedSessionId) setError(message);
      }
      throw caught;
    }
  }, [getToken, refresh]);

  return { views, loading, loaded, error, refresh, unpin };
}
